import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { IonApp, setupIonicReact } from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import Intro from '@pages/auth/pages/Intro'
import '@ionic/react/css/core.css'
import '@ionic/react/css/normalize.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/typography.css'
import '@ionic/react/css/padding.css'
import '@ionic/react/css/float-elements.css'
import '@ionic/react/css/text-alignment.css'
import '@ionic/react/css/text-transformation.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/display.css'
import '@ionic/react/css/palettes/dark.system.css'
import 'react-loading-skeleton/dist/skeleton.css'
import './GlobalStyles.css'
import { ToastProvider } from '@app/ToastProvider'

import { SkeletonTheme } from 'react-loading-skeleton'
import { TrainingConstructorPage } from '@pages/TrainingConstructor'
import { MainPage } from '@pages/Main/ui/page'
import { Profile } from '@pages/Profile/Profile'
import Invite from '@pages/Invite'
import { COLORS } from '@assets/styles/colors'
import { ProfileEditPage } from '@pages/Profile/ProfileEdit'
import { ROUTES } from '@shared/constants/routes'
import { SubscriptionPage } from '@pages/Subscription'
import { CheckExercisePage } from '@pages/CheckExercise/CheckExercise'
import { TrainingResultsPage } from '@pages/exercise/TrainingResult'
import { CardioExecutionPage } from '@pages/exercise/CardioExecutionPage'
import { WorkoutCompletionPage } from '@pages/exercise/WorkoutCompletionPage'
import { RelaxPage } from '@pages/exercise/RelaxPage'
import { ExercisePage } from '@pages/exercise'
import Modal from 'react-modal'
import { CheckTechniquePage } from '@pages/CheckTechnique'
import { PrivacyPolicy } from '@pages/PrivacyPolicy'
import { UserAgreement } from '@pages/UserAgreement'
import { AboutUsPage } from '@pages/AboutUs'
import { IosPwaInstallationGuide } from '@pages/IosPwaInstallationGuide'
import { TimerPage } from '@/pages/timer/Main/page'
import { TimerExecutionPage } from '@/pages/timer/Execution'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Hello from '@pages/auth/pages/Hello'
import { Toaster } from '@/components/ui/sonner'
import { ChatPage } from '@pages/chat/'
import { TestPage } from '@pages/Test'
import { AuthModeProvider } from '@app/providers/authModeProvider'
import { UserServiceContextProvider } from '@app/providers/userServiceProvider'
import { ExerciseFilterStoreProvider } from '@entities/exercise/model/filters.provider'
import { ExercisesStoreProvider } from '@entities/exercise/model/dto.provider'
import {
	ProgramEnrollmentCalendarPage,
	ProgramEnrollmentWeekPage,
	ProgramTrainingHistoryPage,
} from '@pages/program/'
import { AuthInfoPage } from '@pages/auth/pages/AuthInfo'
import { ProgramRedirectPage } from '@pages/program/ui/weekRedirect/page'
import { ExerciseInfoPage } from '@pages/exerciseInfo'
import { EnvPage } from '@pages/Env'
import { LogoutPage } from '@pages/Logout'
import { NativeAppUrlListener } from '@app/NativeAppUrlListener'
import { consumePendingAuthCallback } from '@shared/lib/authRedirect'
import { isNativeAuthPlatform } from '@shared/lib/authRedirect'
import { TrainingDetailsPage } from '@pages/TrainingDetails'
import { AppRouteFooter } from '@app/navigation/AppRouteFooter'
import { initI18n } from '@shared/i18n'

setupIonicReact({})
const queryClient: QueryClient = new QueryClient()
Modal.setAppElement('#root')
const container: HTMLElement | null = document.getElementById('root')
const root: Root = createRoot(container!)
const pendingAuthCallback = consumePendingAuthCallback()
const DEV_CACHE_RESET_RELOAD_KEY = 'tisit_dev_cache_reset_reloaded'

if (pendingAuthCallback) {
	const pendingUrl = new URL(pendingAuthCallback)
	window.history.replaceState(
		null,
		'',
		`${pendingUrl.pathname}${pendingUrl.search}${pendingUrl.hash}`
	)
}

const isNativePlatform = isNativeAuthPlatform()
const isLocalWebHost = ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'].includes(
	window.location.hostname
)

const AnimatedRoutes: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const location = useLocation()
	const history = useHistory()
	const reduceMotion = useReducedMotion()
	const isBack = history.action === 'POP'
	const isReplace = history.action === 'REPLACE'
	const routeKey = location.pathname

	const initialTransform =
		reduceMotion || isReplace
			? 'translate3d(0, 0, 0)'
			: isBack
				? 'translate3d(-12px, 0, 0)'
				: 'translate3d(16px, 0, 0)'
	const exitTransform =
		reduceMotion || isReplace
			? 'translate3d(0, 0, 0)'
			: isBack
				? 'translate3d(18px, 0, 0)'
				: 'translate3d(-8px, 0, 0)'

	return (
		<div className="app-route-stage">
			<AnimatePresence initial={false} mode="wait">
				<motion.div
					key={routeKey}
					className="app-route-frame"
					initial={{ opacity: reduceMotion ? 1 : 0.78, transform: initialTransform }}
					animate={{ opacity: 1, transform: 'translate3d(0, 0, 0)' }}
					exit={{ opacity: reduceMotion ? 1 : 0.68, transform: exitTransform }}
					transition={{
						duration: reduceMotion ? 0.09 : isReplace ? 0.12 : isBack ? 0.25 : 0.27,
						ease: [0.32, 0.72, 0, 1],
					}}>
					<Switch location={location}>{children}</Switch>
				</motion.div>
			</AnimatePresence>
		</div>
	)
}

if (
	!isNativePlatform &&
	import.meta.env.PROD &&
	!isLocalWebHost &&
	'serviceWorker' in navigator
) {
	void navigator.serviceWorker.register('/sw.js')
}

const clearLocalDevRuntimeCache = async () => {
	let didClearRuntimeCache = false

	if ('serviceWorker' in navigator) {
		const registrations = await navigator.serviceWorker.getRegistrations()
		if (registrations.length > 0) {
			didClearRuntimeCache = true
			await Promise.all(registrations.map((registration) => registration.unregister()))
		}
	}

	if ('caches' in window) {
		const keys = await window.caches.keys()
		if (keys.length > 0) {
			didClearRuntimeCache = true
			await Promise.all(keys.map((key) => window.caches.delete(key)))
		}
	}

	if (!didClearRuntimeCache) {
		window.sessionStorage.removeItem(DEV_CACHE_RESET_RELOAD_KEY)
		return
	}

	if (window.sessionStorage.getItem(DEV_CACHE_RESET_RELOAD_KEY) === '1') {
		window.sessionStorage.removeItem(DEV_CACHE_RESET_RELOAD_KEY)
		return
	}

	window.sessionStorage.setItem(DEV_CACHE_RESET_RELOAD_KEY, '1')
	window.location.reload()
}

if (!isNativePlatform && (import.meta.env.DEV || isLocalWebHost)) {
	void clearLocalDevRuntimeCache()
}

const renderApp = () =>
	root.render(
	// <React.StrictMode>
	<IonApp style={{ background: '#fff' }}>
		<ToastProvider />
		<NativeAppUrlListener />
		<IonReactRouter>
			<AuthModeProvider>
				<QueryClientProvider client={queryClient}>
					<UserServiceContextProvider>
						<Toaster />
						<SkeletonTheme baseColor={'#eaeaea'} highlightColor={COLORS.LIGHT_GREY}>
							<AnimatedRoutes>
								<Route exact path={ROUTES.auth.logout}>
									<LogoutPage />
								</Route>
								<Route exact path={'/env'}>
									<EnvPage />
								</Route>
								<Route exact path={ROUTES.test}>
									<TestPage />
								</Route>

								<Route exact path={ROUTES.overall.intro}>
									<Intro />
									{/*<VaultPage />*/}
								</Route>

								<Route exact path={ROUTES.exercise.exerciseInfo}>
									<ExerciseInfoPage />
								</Route>

								<Route exact path={ROUTES.overall.trainingConstructor}>
									<ExerciseFilterStoreProvider>
										<ExercisesStoreProvider>
											<TrainingConstructorPage />
										</ExercisesStoreProvider>
									</ExerciseFilterStoreProvider>
								</Route>

								<Route exact path={ROUTES.programs.exercise}>
									<ExercisePage />
								</Route>

								<Route exact path={ROUTES.programs.execution}>
									<CardioExecutionPage />
								</Route>

								<Route exact path={ROUTES.programs.relax}>
									<RelaxPage />
								</Route>

								<Route exact path={ROUTES.premium.checkExercise}>
									<CheckExercisePage />
								</Route>

								<Route exact path={ROUTES.programs.result}>
									<TrainingResultsPage />
								</Route>

								<Route exact path={ROUTES.training.exercise}>
									<ExercisePage />
								</Route>

								<Route exact path={ROUTES.training.details}>
									<TrainingDetailsPage />
								</Route>

								<Route exact path={ROUTES.training.completed}>
									<WorkoutCompletionPage />
								</Route>

								<Route exact path={ROUTES.auth.enterBaseInfo}>
									<AuthInfoPage />
								</Route>

								<Route exact path={ROUTES.training.cardioExecution}>
									<CardioExecutionPage />
								</Route>

								<Route exact path={ROUTES.training.cardioRelax}>
									<RelaxPage />
								</Route>

								<Route exact path={ROUTES.program.redirectProgram}>
									<ProgramRedirectPage />
								</Route>

								<Route exact path={ROUTES.program.redirectWeek}>
									<ProgramRedirectPage />
								</Route>

								<Route exact path={ROUTES.program.calendar}>
									<ProgramEnrollmentCalendarPage />
								</Route>

								<Route exact path={ROUTES.program.trainingHistory}>
									<ProgramTrainingHistoryPage />
								</Route>

								<Route exact path={ROUTES.program.week}>
									<ProgramEnrollmentWeekPage />
								</Route>

								{/*<Route path={ROUTES.programs.program}>*/}
								{/*	<ProgramPage />*/}
								{/*</Route>*/}

								<Route exact path={ROUTES.overall.main}>
									<MainPage />
								</Route>

								<Route exact path={ROUTES.overall.profile}>
									<Profile />
								</Route>

								<Route exact path={ROUTES.overall.profileEdit}>
									<ProfileEditPage />
								</Route>

								<Route exact path={ROUTES.overall.aiMessenger}>
									<ChatPage />
								</Route>

								<Route exact path={ROUTES.overall.inviteFriends}>
									<Invite />
								</Route>

								<Route exact path={ROUTES.overall.checkTechnique}>
									<CheckTechniquePage />
								</Route>

								<Route exact path={ROUTES.overall.timerDetail}>
									<TimerExecutionPage />
								</Route>
								<Route exact path={ROUTES.overall.timer}>
									<TimerPage />
								</Route>

								{/*<Route exact path={ROUTES.overall.settings}>*/}
								{/*	<SettingsPage />*/}
								{/*</Route>*/}

								<Route exact path={ROUTES.overall.subscription}>
									<SubscriptionPage />
								</Route>

								<Route exact path={ROUTES.overall.privacyPolicy}>
									<PrivacyPolicy />
								</Route>
								<Route exact path={ROUTES.overall.userAgreement}>
									<UserAgreement />
								</Route>
								<Route exact path={ROUTES.overall.aboutUs}>
									<AboutUsPage />
								</Route>
								<Route exact path={ROUTES.overall.pwaInstallationGuide}>
									<IosPwaInstallationGuide />
								</Route>

								<Route exact path='/test'>
									<TestPage />
								</Route>
								<Route exact path={ROUTES.auth.hello}>
									<Hello />
								</Route>
							</AnimatedRoutes>
							<AppRouteFooter />
						</SkeletonTheme>
					</UserServiceContextProvider>
				</QueryClientProvider>
			</AuthModeProvider>
		</IonReactRouter>
	</IonApp>
	// </React.StrictMode>
)

void initI18n().finally(renderApp)
